import { Location } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import { MeteorObservable } from 'meteor-rxjs';
import {
  animationFrames,
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { debounceTime, takeWhile } from 'rxjs/operators';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { Certificates } from 'server/shared/collections/certificates';
import { Customers } from 'server/shared/collections/customers';
import { Employments } from 'server/shared/collections/employments';
import { Specialities } from 'server/shared/collections/specialities';
import { Users } from 'server/shared/collections/users';
import { checkCanCancelEmployment } from 'server/shared/helpers/checkCanCancelEmployment';
import { canTransferEmploymentToCustomer } from 'server/shared/helpers/conditionalTransferHelpers';
import {
  EvaluatedEmploymentStatusEnum,
  getEvaluatedEmploymentStatus,
} from 'server/shared/helpers/getEvaulatedEmploymentStatus';
import { getUsersByCriteriaMongoQuery } from 'server/shared/helpers/mongoQueryParts/getUsersByCriteriaMongoQuery';
import { getUsersByEmploymentAvailabilityMongoQuery } from 'server/shared/helpers/mongoQueryParts/getUsersByEmploymentAvailabilityMongoQuery';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import {
  getUtcStartOfDay,
  removeTimeZoneOffset,
} from 'server/shared/helpers/removeTimeZoneOffset';
import { AssignmentDefault } from 'server/shared/models/assignmentDefault';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Certificate } from 'server/shared/models/certificate';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import {
  CreateEmploymentArgs,
  Employment,
  EmploymentStatusEnum,
  UpdateEmploymentArgs,
  UpdateEmploymentTransferArgs,
} from 'server/shared/models/employment';
import { EmploymentInvitee } from 'server/shared/models/employmentInvitee';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { EmploymentTransfer } from 'server/shared/models/employmentTransferConfig';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import {
  DispoUser,
  MappedCertificate,
  MappedSpeciality,
  PoolerUser,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { SubscriptionService } from 'src/app/shared/subscription.service';
import { getAvailabilityMatchPercentageRange } from 'src/helpers/employment/getAvailabilityMatchPercentage';
import { prepareEmploymentInvitee } from '../../../helpers/employment/prepareEmploymentInvitee';
import { prepareEmploymentUser } from '../../../helpers/employment/prepareEmploymentUser';
import { sortByMatchPercentage } from '../../../helpers/employment/sortByMatchPercentage';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { formControlValue$ } from '../../../helpers/formControlValue$';
import { startMeteorSubscription } from '../../../helpers/startMeteorSubscription';
import { PreparedEmploymentPoolerUser } from '../../../types/employment/PreparedEmploymentPoolerUser';
import { AgentsInfoDialog } from '../../jobs/job-edit/dialogs/agents-info-dialog/agents-info-dialog.component';
import { CustomerService } from '../../services/customer.service';
import { EmploymentService } from '../../services/employment.service';
import { OrganisationService } from '../../services/organisation.service';
import { PoolerService } from '../../services/pooler/pooler.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { UnsavedChangesDeactivate } from '../../shared/guards/unsaved-changes-deactivate.interface';
import { canAccessEmploymentInvitee } from './canAccessEmploymentInvitee';
import { sortByInviteeStatus } from './sortByInviteeStatus';

@Component({
  selector: 'app-employment-edit',
  templateUrl: './employment-edit.component.html',
  styleUrls: ['./employment-edit.component.scss'],
})
export class EmploymentEditComponent
  implements OnInit, OnDestroy, UnsavedChangesDeactivate
{
  roleEnum = RoleEnum;
  i18n = this.translationService.getTranslations('employments');
  loggedInUser?: DispoUser;

  showCompressedEmploymentInfo = false;

  employment?: Employment;
  employment$ = new BehaviorSubject<Employment | undefined>(undefined);
  poolerMap?: PoolerCustomerMap;

  customerOfEmployment$ = new BehaviorSubject<Customer | undefined>(undefined);

  formDisabled = false;
  hasEditPermission = true;
  restrictEdit = false;
  inviteeActionsDisabled = false;
  employmentStatusEnum = EmploymentStatusEnum;

  evaluatedEmploymentStatus$ = combineLatest([
    this.employment$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([employment, customer]) => {
      if (!employment) {
        return EvaluatedEmploymentStatusEnum.PENDING;
      }
      return getEvaluatedEmploymentStatus(employment, customer);
    }),
  );

  invitees$ = combineLatest([
    this.employment$,
    this.poolerService.poolerUsersById$,
    this.customerService.readyDispoCustomer$,
    this.poolerService.readyPoolerCustomerMap$,
  ]).pipe(
    map(([employment, poolerUsersById, currentCustomer, poolerMap]) => {
      return (
        employment?.invitees
          .map((invitee) =>
            prepareEmploymentInvitee(
              invitee,
              poolerUsersById,
              employment,
              currentCustomer,
              poolerMap,
            ),
          )
          .sort(sortByMatchPercentage)
          .sort(sortByInviteeStatus) || []
      );
    }),
    shareReplay(1),
  );

  visibleInvitees$ = combineLatest([
    this.invitees$,
    this.employment$,
    this.customerService.readyDispoCustomer$,
    this.customerService.readySharedPoolCustomers$,
  ]).pipe(
    map(([invitedUsers, employment, currentCustomer, sharedPoolCustomers]) => {
      if (!currentCustomer) {
        return [];
      }
      return (
        invitedUsers
          .filter((invitee) =>
            canAccessEmploymentInvitee(
              invitee,
              currentCustomer,
              employment,
              sharedPoolCustomers,
            ),
          )
          .sort(sortByMatchPercentage)
          .sort(sortByInviteeStatus) || []
      );
    }),
    shareReplay(1),
  );

  // render 1 invitee id per animation frame
  renderedInviteeIds$ = this.visibleInvitees$.pipe(
    map((invitees) => invitees.map((i) => i.id)),
    map((allIds) => ({
      idsToRender: new Set<string>(allIds),
      renderedIds: new Set<string>(),
    })),
    switchMap(({ idsToRender, renderedIds }) => {
      return animationFrames().pipe(
        takeWhile(
          () => idsToRender.size > 0,
          true, // inclusive so it emits the final state
        ),
        map(() => ({ idsToRender, renderedIds })),
      );
    }),
    map(({ idsToRender, renderedIds }) => {
      // move one ID to rendered
      const idToRender = idsToRender.values().next().value;
      if (idToRender) {
        renderedIds.add(idToRender);
        idsToRender.delete(idToRender);
      }
      return renderedIds;
    }),
    shareReplay(1),
  );

  employmentId?: string;

  canCancel$ = combineLatest([
    this.employment$.pipe(filter(filterIsNotEmpty)),
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([employment, customer]) =>
      checkCanCancelEmployment(employment, customer),
    ),
  );

  startDateControl = new FormControl('', Validators.required);
  endDateControl = new FormControl('', Validators.required);
  workloadControl = new FormControl(1, Validators.required);
  selectedDepartmentIdControl = new FormControl('', Validators.required);
  editorIdControl = new FormControl('', Validators.required);
  descriptionControl = new FormControl('');
  requiredUsersControl = new FormControl(1);

  carControl = new FormControl('');
  languageControl = new FormControl('');
  // contains mappedCertificates as value
  certificatesControl = new FormControl([]);
  // contains mappedSpecialities as value
  specialitiesControl = new FormControl([]);
  sexControl = new FormControl('');
  includeUndecidedAvailabilityControl = new FormControl(false);
  ignoreUserPreferenceDepartmentBlacklistControl = new FormControl(false);
  selectAllUsersControl = new FormControl(false);

  // this form should mirror the shape of Employment
  form = new FormGroup({
    description: this.descriptionControl,
    editorId: this.editorIdControl,
    selectedDepartmentId: this.selectedDepartmentIdControl,
    startDate: this.startDateControl,
    endDate: this.endDateControl,
    workload: this.workloadControl,
    car: this.carControl,
    language: this.languageControl,
    certificates: this.certificatesControl,
    specialities: this.specialitiesControl,
    sex: this.sexControl,
    requiredUsers: this.requiredUsersControl,
  });

  transferToMe?: EmploymentTransfer;
  transferToMe$ = new BehaviorSubject<EmploymentTransfer | undefined>(
    undefined,
  );

  employmentTracker?: Tracker.Computation;
  isNewEmployment = false;

  private destroyed$ = new Subject<void>();
  private mainTracker?: Tracker.Computation;

  sexControlValue$ = formControlValue$(this.sexControl);
  carControlValue$ = formControlValue$(this.carControl);
  languageControlValue$ = formControlValue$(this.languageControl);
  certificatesControlValue$ = formControlValue$<MappedCertificate[]>(
    this.certificatesControl,
  );
  specialitiesControlValue$ = formControlValue$<MappedSpeciality[]>(
    this.specialitiesControl,
  );

  hasDeviatingSex$ = combineLatest([
    this.sexControlValue$,
    this.employment$,
    this.transferToMe$,
  ]).pipe(
    map(
      ([sex, employment, transferToMe]) =>
        transferToMe && sex !== employment?.sex,
    ),
  );

  deviatingSexValue$ = combineLatest([
    this.hasDeviatingSex$,
    this.employment$,
  ]).pipe(
    map(([hasDeviating, employment]) =>
      hasDeviating ? employment?.sex : undefined,
    ),
  );

  hasDeviatingCar$ = combineLatest([
    this.carControlValue$,
    this.employment$,
    this.transferToMe$,
  ]).pipe(
    map(
      ([car, employment, transferToMe]) =>
        transferToMe && car !== employment?.car,
    ),
  );

  deviatingCarValue$ = combineLatest([
    this.hasDeviatingCar$,
    this.employment$,
  ]).pipe(
    map(([hasDeviating, employment]) =>
      hasDeviating ? employment?.car : undefined,
    ),
  );

  hasDeviatingLanguage$ = combineLatest([
    this.languageControlValue$,
    this.employment$,
    this.transferToMe$,
  ]).pipe(
    map(
      ([language, employment, transferToMe]) =>
        transferToMe && language !== employment?.language,
    ),
  );

  deviatingLanguageValue$ = combineLatest([
    this.hasDeviatingLanguage$,
    this.employment$,
  ]).pipe(
    map(([hasDeviating, employment]) =>
      hasDeviating ? employment?.language : undefined,
    ),
  );

  hasDeviatingCertificates$ = combineLatest([
    this.certificatesControlValue$,
    this.employment$,
    this.transferToMe$,
  ]).pipe(
    map(
      ([certificates, employment, transferToMe]) =>
        transferToMe &&
        (certificates?.length !== employment?.certificates.length ||
          !certificates?.every(
            (c, i) => employment?.certificates[i]?.id === c.id,
          )),
    ),
  );
  deviatingCertificatesValue$ = combineLatest([
    this.hasDeviatingCertificates$,
    this.employment$,
  ]).pipe(
    filter(([hasDeviating]) => Boolean(hasDeviating)),
    map(([_, employment]) => {
      const employmentCertificates = employment?.certificates || [];
      return (
        employmentCertificates
          .map((c: MappedCertificate) => c.displayName)
          .join(', ') || this.i18n.empty
      );
    }),
  );

  hasDeviatingSpecialities$ = combineLatest([
    this.specialitiesControlValue$,
    this.employment$,
    this.transferToMe$,
  ]).pipe(
    map(
      ([specialities, employment, transferToMe]) =>
        transferToMe &&
        (specialities?.length !== employment?.specialities.length ||
          !specialities?.every(
            (s, i) => employment?.specialities[i]?.id === s.id,
          )),
    ),
  );

  deviatingSpecialitiesValue$ = combineLatest([
    this.hasDeviatingSpecialities$,
    this.employment$,
  ]).pipe(
    filter(([hasDeviating]) => Boolean(hasDeviating)),
    map(([_, employment]) => {
      const employmentSpecialities = employment?.specialities || [];
      return (
        employmentSpecialities
          .map((c: MappedSpeciality) => c.displayName)
          .join(', ') || this.i18n.empty
      );
    }),
  );

  usersCriteriaQuery$ = combineLatest([
    this.carControlValue$,
    this.languageControlValue$,
    this.certificatesControlValue$,
    this.specialitiesControlValue$,
    this.sexControlValue$,
  ]).pipe(
    map(([car, language, certificates, specialities, sex]) => {
      return getUsersByCriteriaMongoQuery({
        car,
        language,
        certificates,
        specialities,
        sex,
      });
    }),
  );

  hasRequiredFieldsForQuery$ = combineLatest([
    formControlValue$(this.startDateControl),
    formControlValue$(this.endDateControl),
    formControlValue$(this.selectedDepartmentIdControl),
    formControlValue$(this.workloadControl),
  ]).pipe(
    map(([startDate, endDate, departmentId, workload]) => {
      return !!startDate && !!endDate && !!departmentId && !!workload;
    }),
  );

  usersAvailabilityQuery$ = combineLatest([
    formControlValue$(this.startDateControl),
    formControlValue$(this.endDateControl),
    formControlValue$(this.includeUndecidedAvailabilityControl),
  ]).pipe(
    map(([startDate, endDate, includeUndecided]) => {
      return getUsersByEmploymentAvailabilityMongoQuery({
        startDate: format(new Date(startDate), dbDayDateToken),
        endDate: format(new Date(endDate), dbDayDateToken),
        includeUndecided,
      });
    }),
  );

  possibleUsers$ = combineLatest([
    this.hasRequiredFieldsForQuery$,
    this.customerService.dispoCustomer$,
    this.poolerService.readyEmploymentSharedPoolCustomers$,
    this.usersCriteriaQuery$,
    this.usersAvailabilityQuery$,
    formControlValue$(this.startDateControl),
    formControlValue$(this.selectedDepartmentIdControl),
    formControlValue$(this.ignoreUserPreferenceDepartmentBlacklistControl),
    this.invitees$,
    this.customerOfEmployment$,
  ]).pipe(
    switchMap(
      ([
        hasRequiredFields,
        customer,
        sharedPoolCustomers,
        usersCriteriaQuery,
        usersAvailabilityQuery,
        startDate,
        departmentId,
        ignoreUserPreferenceDepartmentBlacklist,
        alreadyInvitedUsers,
        customerOfEmployment,
      ]) => {
        if (!customer || !hasRequiredFields) {
          return [];
        }
        let customerOfEmploymentQuery: Mongo.Query<PoolerUser> = {};
        if (customerOfEmployment?.requiredEmploymentFields) {
          if (customerOfEmployment.requiredEmploymentFields.dateOfBirth) {
            customerOfEmploymentQuery['dateOfBirth'] = { $exists: true };
          }
        }
        return this.poolerService.getPoolers({
          _id: {
            $nin: alreadyInvitedUsers
              .map((u) => u.userId)
              .filter(filterIsNotEmpty),
          },
          $and: [
            usersCriteriaQuery,
            usersAvailabilityQuery,
            {
              $or: [customer, ...sharedPoolCustomers].map((c) => ({
                _id: {
                  $in: [...(c?.pool.activeUserIds || [])],
                },
                [`customerInformation.${c._id}.enableEmployments`]: true,
                $or: [
                  {
                    [`customerInformation.${c._id}.joinDate`]: {
                      $lte: format(startDate, dbDayDateToken),
                    },
                  },
                  {
                    [`customerInformation.${c._id}.joinDate`]: {
                      $exists: false,
                    },
                  },
                ],
              })),
            },
            {
              $or: [
                // own customer department match
                (() => {
                  const customerDepartmentQuery = {
                    [`customerInformation.${customer._id}.departmentIds`]:
                      departmentId,
                  };
                  if (
                    // is not ignored
                    !ignoreUserPreferenceDepartmentBlacklist ||
                    // customer dow not allow ignoring
                    !customer.allowIgnoreUserPreferenceDepartmentBlacklist
                  ) {
                    customerDepartmentQuery[
                      'userPreferenceDepartmentBlacklist'
                    ] = { $ne: departmentId };
                  }
                  return customerDepartmentQuery;
                })(),
                // shared pool customer department match
                ...sharedPoolCustomers.map((c) => ({
                  [`customerInformation.${c._id}.departmentIds`]: departmentId,
                  userPreferenceDepartmentBlacklist: { $ne: departmentId },
                })),
              ],
            },
            {
              $or: [
                { _id: { $in: customer.pool.activeUserIds } },
                customerOfEmploymentQuery,
              ],
            },
          ],
        });
      },
    ),
    debounceTime(50),
    // Makes sure the pipe is shared between different subscribers / async pipes
    shareReplay(1),
  );

  possibleUsersWithMatch$ = combineLatest([
    combineLatest([
      formControlValue$(this.startDateControl),
      formControlValue$(this.endDateControl),
    ]).pipe(
      map(([startDate, endDate]) =>
        getAvailabilityMatchPercentageRange(startDate, endDate),
      ),
      shareReplay(1),
    ),
    formControlValue$(this.workloadControl),
    this.possibleUsers$,
    this.customerService.readyDispoCustomer$,
    this.poolerService.readyPoolerCustomerMap$,
    formControlValue$(this.selectedDepartmentIdControl),
  ]).pipe(
    debounceTime(50),
    map(
      ([
        matchPercentageRange,
        workload,
        users,
        currentCustomer,
        poolerMap,
        departmentId,
      ]): PreparedEmploymentPoolerUser[] => {
        return users
          .map((user) =>
            prepareEmploymentUser(
              user,
              matchPercentageRange,
              workload,
              currentCustomer,
              poolerMap,
              departmentId,
            ),
          )
          .sort(this.sortByName)
          .sort(sortByMatchPercentage);
      },
    ),
    shareReplay(1),
  );

  sortByName = (
    a: PreparedEmploymentPoolerUser,
    b: PreparedEmploymentPoolerUser,
  ) => {
    const loggedInCustomer = this.loggedInCustomer;
    if (loggedInCustomer?.prioritizeEmployeeLastName) {
      return a.lastName.localeCompare(b.lastName);
    } else {
      return a.firstName.localeCompare(b.firstName);
    }
  };

  selectedInviteesMap = new Map<string, PoolerUser>();
  selectedInvitees: PoolerUser[] = [];

  usersOfEmploymentSubscription?: Subscription;

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private organisationService: OrganisationService,
    private customerService: CustomerService,
    private poolerService: PoolerService,
    private employmentService: EmploymentService,
    private router: Router,
    private dialog: MatDialog,
    private subscriptionService: SubscriptionService,
  ) {
    this.renderedInviteeIds$.subscribe();
    this.subscriptionService.employmentUsersReady$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  transfers$ = new BehaviorSubject<EmploymentTransfer[]>([]);

  transferAll = false;
  transferredAll = false;

  loggedInCustomer?: Customer;

  paramCertificates: string[] = [];
  paramSpecialities: string[] = [];
  ngOnInit(): void {
    this.mainTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.loggedInUser = Users.findOne({
          _id: Meteor.userId(),
          role: RoleEnum.DISPO,
        }) as DispoUser;
        if (!this.loggedInUser) {
          return;
        }
        this.loggedInCustomer = Customers.findOne({
          _id: this.loggedInUser?.customerId,
        });
        // TODO Fix this, without this you cannot invite on creation
        this.customerOfEmployment$.next(this.loggedInCustomer);

        this.poolerService.readyPoolerCustomerMap$
          .pipe(takeUntil(this.destroyed$))
          .subscribe((poolerMap) => {
            this.poolerMap = poolerMap;
          });

        this.route.params
          .pipe(takeUntil(this.destroyed$))
          .subscribe((params) => {
            this.loadEmploymentById(
              params.employmentId,
              this.route.snapshot.queryParams,
            ).then();
          });
      });
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.usersOfEmploymentSubscription) {
      this.usersOfEmploymentSubscription.unsubscribe();
    }
    if (this.employmentTracker) {
      this.employmentTracker.stop();
    }
    if (this.mainTracker) {
      this.mainTracker.stop();
    }
  }

  async loadEmploymentById(employmentId: string, queryParams: Params) {
    const loggedInUser = this.loggedInUser;
    if (!loggedInUser) return;
    if (!employmentId)
      throw new Error('Employment ID is missing for employment-edit');
    const isNewEmployment = employmentId === 'new';
    this.isNewEmployment = isNewEmployment;
    this.showCompressedEmploymentInfo = !isNewEmployment;
    if (isNewEmployment) {
      delete this.employmentId;
      this.setNewEmploymentDefaultValues(queryParams);
    } else {
      this.employmentId = employmentId;
      await startMeteorSubscription('employmentItem', employmentId);
      if (this.employmentTracker) {
        this.employmentTracker.stop();
      }
      this.employmentTracker = Tracker.autorun(() => {
        this.ngZone.run(() => {
          const employment = Employments.findOne({
            _id: employmentId,
          });

          if (!employment) {
            // subscription not complete yet
            return;
          }

          let customerOfEmployment = Customers.findOne({
            _id: employment.customer.id,
          });
          this.transferToMe = employment.transfers.find(
            (t) => t.targetCustomer.id === loggedInUser.customerId,
          );
          this.transferToMe$.next(this.transferToMe);
          this.transfers$.next(employment.transfers);
          this.transferredAll = employment.transfers.every(
            (transfer) => transfer.transferredAt,
          );
          this.employment = employment;
          if (
            customerOfEmployment?.customerTransfers?.transferConditions
              ?.employmentTransferConditions
          ) {
            this.setConditionalTransfer();
          }
          this.employment$.next(employment);
          this.subscribeUsersOfEmployment(employment._id);
          this.customerOfEmployment$.next(customerOfEmployment);
          this.patchForm(employment);
        });
      });
    }
  }

  subscribeUsersOfEmployment(employmentId: string) {
    if (this.usersOfEmploymentSubscription) {
      this.usersOfEmploymentSubscription.unsubscribe();
    }
    this.usersOfEmploymentSubscription = MeteorObservable.subscribe(
      'usersOfEmployment',
      employmentId,
      false,
    ).subscribe();
  }

  setNewEmploymentDefaultValues(queryParams: Params) {
    // when duplicating an employment, the data gets set to the queryParams

    this.descriptionControl.setValue('');
    this.carControl.setValue('');
    this.languageControl.setValue('');

    this.specialitiesControl.setValue([]);
    this.sexControl.setValue('');

    if (queryParams.workload) {
      this.workloadControl.setValue(Number(queryParams.workload));
      this.workloadControl.markAsDirty();
    } else {
      this.workloadControl.setValue(100);
    }

    if (queryParams.startDate) {
      this.startDateControl.setValue(new Date(queryParams.startDate));
      this.startDateControl.markAsDirty();
      this.initializeNewEmploymentTransfers();
    } else {
      this.startDateControl.setValue(null);
    }

    if (queryParams.endDate) {
      this.endDateControl.setValue(new Date(queryParams.endDate));
      this.endDateControl.markAsDirty();
    } else {
      this.endDateControl.setValue(null);
    }

    if (queryParams.departmentId) {
      this.selectedDepartmentIdControl.setValue(queryParams.departmentId);
      this.selectedDepartmentIdControl.markAsDirty();
      this.setDefaultValues(queryParams.departmentId);
    } else {
      this.selectedDepartmentIdControl.setValue('');
    }

    if (queryParams.editorId) {
      this.editorIdControl.setValue(queryParams.editorId);
      this.editorIdControl.markAsDirty();
    } else {
      this.editorIdControl.setValue(Meteor.userId());
    }

    // description
    if (queryParams.description !== undefined) {
      this.descriptionControl.setValue(
        decodeURIComponent(queryParams.description),
      );
      this.descriptionControl.markAsDirty();
    } else {
      this.descriptionControl.setValue('');
    }

    // sex
    if (queryParams.sex !== undefined) {
      this.sexControl.setValue(Number(queryParams.sex));
      this.sexControl.markAsDirty();
    } else {
      this.sexControl.setValue('');
    }

    // car
    if (queryParams.car !== undefined) {
      this.carControl.setValue(Number(queryParams.car));
      this.carControl.markAsDirty();
    } else {
      this.carControl.setValue('');
    }

    // language
    if (queryParams.language !== undefined) {
      this.languageControl.setValue(Number(queryParams.language));
      this.languageControl.markAsDirty();
    } else {
      this.languageControl.setValue('');
    }

    // specialities
    if (queryParams.specialities) {
      this.paramSpecialities = queryParams.specialities.split(',');
      const specialities = Specialities.find({
        _id: { $in: this.paramSpecialities },
      }).fetch();
      this.specialitiesControl.setValue(
        specialities.map((c) => ({
          id: c._id,
          displayName: c.name,
        })),
      );
      this.specialitiesControl.markAsDirty();
    } else {
      this.specialitiesControl.setValue([]);
    }

    // certificates
    if (queryParams.certificates) {
      this.paramCertificates = queryParams.certificates.split(',');
      const certificates = Certificates.find({
        _id: { $in: this.paramCertificates },
      }).fetch();
      this.certificatesControl.setValue(
        certificates.map((c) => ({
          id: c._id,
          displayName: c.name,
        })),
      );
      this.certificatesControl.markAsDirty();
    } else {
      this.certificatesControl.setValue([]);
    }

    if (queryParams.poolerId) {
      this.poolerService.poolerUsersById$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((map) => {
          const poolerUser = map.get(queryParams.poolerId);
          if (poolerUser) {
            this.toggleInvitedUser(poolerUser);
          }
        });
    }
  }

  patchForm(employment: Employment) {
    this.form.patchValue({
      startDate: new Date(employment.startDate),
      endDate: new Date(employment.endDate),
      workload: employment.workload,
      editorId: employment.editor.userId,
      selectedDepartmentId: employment.department.id,
      description: employment.description,
      requiredUsers: employment.requiredUsers,
      specialities:
        this.transferToMe?.criteria?.specialities ?? employment.specialities,
      certificates:
        this.transferToMe?.criteria?.certificates ?? employment.certificates,
      car: this.transferToMe?.criteria?.car ?? employment.car,
      language: this.transferToMe?.criteria?.language ?? employment.language,
      sex: this.transferToMe?.criteria?.sex ?? employment.sex,
    });
    this.formDisabled = this.canEditEmployment(employment);
    this.hasEditPermission = this.hasEmploymentEditPermissions(employment);
    this.restrictEdit = this.restrictEditEmployment(employment);
    this.inviteeActionsDisabled = this.canUseInviteeActions(employment);
    if (this.formDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    if (this.restrictEdit) {
      this.editorIdControl.disable();
      this.selectedDepartmentIdControl.disable();
      this.descriptionControl.disable();
      this.certificatesControl.disable();
      this.specialitiesControl.disable();
      this.carControl.disable();
      this.languageControl.disable();
      this.sexControl.disable();
      this.requiredUsersControl.disable();
    }
    if (!this.hasEditPermission) {
      this.requiredUsersControl.disable();
    }
  }

  hasEmploymentEditPermissions(employment: Employment) {
    const loggedInUser = this.loggedInUser;
    if (!loggedInUser) return false;
    return loggedInUser.customerId === employment.customer.id;
  }

  canEditEmployment(employment: Employment) {
    return (
      employment.status === EmploymentStatusEnum.CANCELED ||
      employment.status === EmploymentStatusEnum.ARCHIVED
    );
  }

  restrictEditEmployment(employment: Employment) {
    return employment.status === EmploymentStatusEnum.CLOSED;
  }

  canUseInviteeActions(employment: Employment) {
    return (
      employment.status === EmploymentStatusEnum.CANCELED ||
      employment.status === EmploymentStatusEnum.ARCHIVED
    );
  }

  selectDepartment(department?: Department) {
    const oldDepartmentId = this.selectedDepartmentIdControl.value;
    this.selectedDepartmentIdControl.setValue(department?.id);
    this.selectedDepartmentIdControl.markAsDirty();

    this.setDefaultValues(department?.id || '', oldDepartmentId);
  }

  setDefaultValues(departmentId: string, oldDepartmentId?: string) {
    let oldDefaultValues: AssignmentDefault | undefined =
      this.loggedInCustomer?.defaultEmploymentValues?.find(
        (defaultJobValue) => defaultJobValue.department.id === oldDepartmentId,
      );
    if (oldDefaultValues) {
      if (
        this.descriptionControl.value === oldDefaultValues.values?.description
      ) {
        this.descriptionControl.setValue('');
        this.descriptionControl.markAsDirty();
      }

      if (
        this.certificatesControl.value.every((certificate: MappedCertificate) =>
          oldDefaultValues?.values?.certificates?.some(
            (oldDefaultCertificate) =>
              oldDefaultCertificate.id === certificate.id,
          ),
        ) &&
        !this.paramCertificates.length
      ) {
        this.certificatesControl.setValue([]);
        this.certificatesControl.markAsDirty();
      }
      if (
        this.specialitiesControl.value.every((speciality: MappedSpeciality) =>
          oldDefaultValues?.values?.specialities?.some(
            (oldDefaultSpeciality) => oldDefaultSpeciality.id === speciality.id,
          ),
        ) &&
        !this.paramSpecialities.length
      ) {
        this.specialitiesControl.setValue([]);
        this.certificatesControl.markAsDirty();
      }
    }

    let defaultValues: AssignmentDefault | undefined =
      this.loggedInCustomer?.defaultEmploymentValues?.find(
        (defaultJobValue) => defaultJobValue.department.id === departmentId,
      );
    if (this.isNewEmployment && defaultValues) {
      if (defaultValues.values?.description) {
        this.descriptionControl.setValue(defaultValues.values.description);
        this.descriptionControl.markAsDirty();
      }
      if (
        defaultValues.values?.certificates &&
        !this.paramCertificates.length
      ) {
        this.certificatesControl.setValue(defaultValues.values.certificates);
        this.certificatesControl.markAsDirty();
      }
      if (defaultValues.values?.specialities) {
        this.specialitiesControl.setValue(defaultValues.values.specialities);
        this.specialitiesControl.markAsDirty();
      }
    }
  }

  selectStartDate($event: MatDatepickerInputEvent<any, any>) {
    this.startDateControl.setValue($event.value);
    this.startDateControl.markAsDirty();
    this.initializeNewEmploymentTransfers();
  }

  selectEndDate($event: MatDatepickerInputEvent<any, any>) {
    this.endDateControl.setValue($event.value);
    this.endDateControl.markAsDirty();
  }

  async saveEmployment() {
    if (
      !this.form.valid ||
      (this.loggedInCustomer?.requiredEmploymentFields?.certificates &&
        !this.certificatesControl.value.length)
    ) {
      this.toastService.add({
        type: 'INFO',
        message: 'InvalidForm',
      });
      return;
    }
    let newJobId: string | undefined = undefined;
    if (this.form.dirty) {
      if (this.isNewEmployment) {
        newJobId = await this.createEmployment();
        this.employmentId = newJobId;
        this.form.markAsPristine();
        this.toastService.add({
          type: 'SUCCESS',
          message: 'employmentCreated',
        });
      } else {
        if (this.transferToMe) {
          await this.updateEmploymentTransfer();
        } else {
          await this.updateEmployment();
        }
        this.form.markAsPristine();
        this.toastService.add({
          type: 'SUCCESS',
          message: 'saved',
        });
      }
    }
    if (
      this.selectedInvitees.length > 0 &&
      !this.restrictEdit &&
      !this.formDisabled
    ) {
      if (!this.employmentId) {
        throw new Error('Missing employment ID for inviting users');
      }
      let preparedInvitees: PreparedEmploymentPoolerUser[] =
        this.selectedInvitees.map((inviteeUser) => {
          let originCustomer;
          let customerOfUser = this.poolerMap?.getCustomerOfUser(
            inviteeUser._id,
          );
          if (customerOfUser)
            originCustomer = {
              id: customerOfUser._id,
              displayName: customerOfUser.name,
            };
          return {
            ...inviteeUser,
            originCustomer,
          };
        });
      await this.employmentService.inviteUsersToEmployment(
        this.employmentId,
        preparedInvitees,
      );
      this.ngZone.run(() => {
        this.toastService.add({
          type: 'SUCCESS',
          message: 'employeesInvitedSuccessfully',
        });
        this.resetSelectedInvitees();
      });
    }
    this.submitDirectTransfers();
    if (newJobId) {
      await this.router.navigate(['/temp-work/employments', newJobId]);
    }
  }

  submitDirectTransfers() {
    const isOwnEmployment =
      this.isNewEmployment ||
      (this.employment &&
        this.employment.customer.id === this.loggedInCustomer?._id);
    if (!isOwnEmployment) {
      return;
    }
    const openDirectTransfers = this.transfers$.value.filter(
      (t) => !t.transferredAt && t.directTransfer,
    );
    if (openDirectTransfers.length > 0) {
      openDirectTransfers.forEach((transfer) => {
        this.transferEmployment(transfer);
      });
    }
  }

  async updateEmploymentTransfer() {
    if (!this.employmentId) {
      this.toastService.add({
        type: 'ERROR',
        message: 'noEmploymentIdForUpdate',
      });
      return;
    }
    const updateTransferObject: UpdateEmploymentTransferArgs = {
      employmentId: this.employmentId,
    };
    if (this.carControl.dirty && !this.restrictEdit) {
      updateTransferObject.car = this.carControl.value;
    }
    if (this.languageControl.dirty && !this.restrictEdit) {
      updateTransferObject.language = this.languageControl.value;
    }
    if (this.certificatesControl.dirty && !this.restrictEdit) {
      updateTransferObject.certificates = this.certificatesControl.value;
    }
    if (this.specialitiesControl.dirty && !this.restrictEdit) {
      updateTransferObject.specialities = this.specialitiesControl.value;
    }
    if (this.sexControl.dirty && !this.restrictEdit) {
      updateTransferObject.sex = this.sexControl.value;
    }
    if (this.editorIdControl.dirty && !this.restrictEdit) {
      updateTransferObject.editorId = this.editorIdControl.value;
    }
    await this.employmentService.updateEmploymentTransfer(updateTransferObject);
  }

  async updateEmployment() {
    if (!this.employmentId) {
      this.toastService.add({
        type: 'ERROR',
        message: 'noEmploymentIdForUpdate',
      });
      return;
    }
    const updateObject: UpdateEmploymentArgs = {
      _id: this.employmentId,
    };
    if (this.descriptionControl.dirty && !this.restrictEdit) {
      updateObject.description = this.descriptionControl.value;
    }
    if (this.carControl.dirty && !this.restrictEdit) {
      updateObject.car = this.carControl.value;
    }
    if (this.languageControl.dirty && !this.restrictEdit) {
      updateObject.language = this.languageControl.value;
    }
    if (this.certificatesControl.dirty && !this.restrictEdit) {
      updateObject.certificates = this.certificatesControl.value;
    }
    if (this.specialitiesControl.dirty && !this.restrictEdit) {
      updateObject.specialities = this.specialitiesControl.value;
    }
    if (this.sexControl.dirty && !this.restrictEdit) {
      updateObject.sex = this.sexControl.value;
    }
    if (this.requiredUsersControl.dirty && !this.restrictEdit) {
      updateObject.requiredUsers = this.requiredUsersControl.value;
    }
    if (this.editorIdControl.dirty && !this.restrictEdit) {
      updateObject.editor = this.getEmploymentEditorForUpdate(
        this.editorIdControl.value,
      );
    }
    if (this.transfers$.value.length > 0 && !this.restrictEdit) {
      updateObject.transfers = this.transfers$.value;
    }

    if (this.startDateControl.dirty) {
      updateObject.startDate = format(
        new Date(this.startDateControl.value),
        dbDayDateToken,
      );
    }
    if (this.endDateControl.dirty) {
      updateObject.endDate = format(
        new Date(this.endDateControl.value),
        dbDayDateToken,
      );
    }
    if (this.workloadControl.dirty) {
      updateObject.workload = this.workloadControl.value;
    }
    await this.employmentService.updateEmployment(updateObject);
  }

  async createEmployment() {
    let employmentArgs = await this.getEmploymentArgs();
    if (!employmentArgs) return;
    // returns the ID of the newly created job
    return await this.employmentService.createEmployment(employmentArgs);
  }

  async getEmploymentArgs(): Promise<CreateEmploymentArgs | undefined> {
    const selectedDepartmentId = this.selectedDepartmentIdControl.value;
    const departmentAndOrganisation =
      await this.organisationService.getOrganisationAndDepartmentById(
        selectedDepartmentId,
      );
    if (!departmentAndOrganisation) {
      this.toastService.add({
        type: 'ERROR',
        message: 'noDepartmentAndOrganisationFound',
      });
      return;
    }

    return {
      editor: this.getEmploymentEditorForUpdate(this.editorIdControl.value),
      department: {
        id: departmentAndOrganisation.department.id,
        name: departmentAndOrganisation.department.name,
        phone: departmentAndOrganisation.department.phone,
        costCenter: departmentAndOrganisation.department.costCenter,
      },
      organisation: {
        id: departmentAndOrganisation.organisation._id,
        name: departmentAndOrganisation.organisation.name,
        address: departmentAndOrganisation.organisation.address,
      },
      startDate: format(
        removeTimeZoneOffset(new Date(this.startDateControl.value)),
        dbDayDateToken,
      ),
      endDate: format(
        removeTimeZoneOffset(new Date(this.endDateControl.value)),
        dbDayDateToken,
      ),
      workload: this.workloadControl.value,
      description: this.descriptionControl.value,
      sex: this.sexControl.value,
      car: this.carControl.value,
      language: this.languageControl.value,
      certificates: this.certificatesControl.value,
      specialities: this.specialitiesControl.value,
      requiredUsers: this.requiredUsersControl.value,
      transfers: this.transfers$.value || [],
    };
  }

  getEmploymentEditorForUpdate(userId: string): CreateEmploymentArgs['editor'] {
    const editorUser = Users.findOne({
      _id: userId,
    });
    if (!editorUser) {
      throw new Error('Missing editor user');
    }
    return {
      userId: editorUser._id,
      firstName: editorUser.firstName,
      lastName: editorUser.lastName,
    };
  }

  certificatesSelected(newValue: Certificate[]) {
    let mappedCertificates: MappedCertificate[] = newValue.map((c) => ({
      id: c._id,
      displayName: c.name,
    }));
    this.certificatesControl.setValue(mappedCertificates);
    this.certificatesControl.markAsDirty();
    this.certificatesControl.markAsTouched();

    if (
      this.customerOfEmployment$.value?.customerTransfers?.transferConditions
        ?.employmentTransferConditions
    ) {
      this.setConditionalTransfer();
    }
  }

  specialitiesSelected(newValue: Certificate[]) {
    const mappedSpecialities = newValue.map((c) => ({
      id: c._id,
      displayName: c.name,
    }));
    this.specialitiesControl.setValue(mappedSpecialities);
    this.specialitiesControl.markAsDirty();
    this.specialitiesControl.markAsTouched();
  }

  historyBack() {
    this.location.back();
  }

  toggleInvitedUser(user: PoolerUser, possibleUsers?: PoolerUser[]) {
    if (this.selectedInviteesMap.get(user._id)) {
      this.selectedInviteesMap.delete(user._id);
    } else {
      this.selectedInviteesMap.set(user._id, user);
    }
    this.selectedInvitees = Array.from(this.selectedInviteesMap.values());

    if (this.selectedInvitees.length === possibleUsers?.length) {
      this.selectAllUsersControl.setValue(true);
    } else if (this.selectAllUsersControl.value) {
      this.selectAllUsersControl.setValue(false);
    }
  }

  resetSelectedInvitees() {
    this.selectedInviteesMap.clear();
    this.selectedInvitees = [];
  }

  async cancelEmployment() {
    if (!this.employment) {
      throw new Error('Missing employment to cancel');
    }
    await this.employmentService.cancelEmployment(
      this.employment._id,
      this.loggedInCustomer?.cancelReasons || defaultCancelReasons,
    );
  }

  readonly EvaluatedEmploymentStatusEnum = EvaluatedEmploymentStatusEnum;
  readonly EmploymentStatusEnum = EmploymentStatusEnum;

  private initializeNewEmploymentTransfers() {
    this.customerService.readyDispoCustomer$
      .pipe(take(1))
      .subscribe((customer) => {
        const transferConfigs = customer.customerTransfers?.employmentTransfers;
        if (!transferConfigs) {
          return;
        }
        this.transfers$.next(
          transferConfigs.map((t) => {
            return {
              targetCustomer: t.targetCustomer,
              directTransfer: t.directTransfer || false,
              forceDirectAccept: t.forceDirectAccept || false,
              preventDirectAccept: t.preventDirectAccept || false,
              hideDirectAccept: t.hideDirectAccept || false,
              auto: t.auto || false,
              availabilityInfosForAgents: t.availabilityInfosForAgents,
              transferAt: addDays(
                getUtcStartOfDay(new Date(this.startDateControl.value)),
                (t.redirectOffset || 0) * -1,
              ),
            };
          }),
        );
      });
  }

  transferEmployment(transfer: EmploymentTransfer) {
    if (!this.employmentId) {
      throw new Error('Missing employment ID for transfer');
    }
    this.employmentService.transferEmployment(this.employmentId, transfer);
  }

  directTransferCheckboxChange(transfer: EmploymentTransfer) {
    if (!this.loggedInUser) return;

    transfer.directTransfer = !transfer.directTransfer;
    this.transferAll =
      this.transfers$.value.every((transfer) => transfer.directTransfer) ||
      false;
  }

  transferAutoCheckboxChange(transfer: EmploymentTransfer) {
    if (!this.loggedInUser) return;

    transfer.auto = !transfer.auto;
    transfer.transferAt;
    this.form.markAsTouched();
    this.form.markAsDirty();
  }

  manualTransferAll() {
    if (!this.employmentId) {
      throw new Error('Missing employment ID for transfer');
    }
    this.employmentService.transferEmploymentToAll(this.employmentId);
  }

  directTransferAllCheckboxChange() {
    this.transfers$.value.forEach((transfer) => {
      if (!this.loggedInUser) return;
      transfer.directTransfer = !this.transferAll;
    });
    this.transferAll = !this.transferAll;
  }

  toggleSelectAllUsers(possibleUsers: PreparedEmploymentPoolerUser[]) {
    if (this.selectedInviteesMap.size === possibleUsers.length) {
      this.selectedInviteesMap.clear();
    } else {
      possibleUsers
        .filter((u) => !u.hasBlacklistedJobDepartment)
        .forEach((user) => {
          this.selectedInviteesMap.set(user._id, user);
        });
    }
    this.selectedInvitees = Array.from(this.selectedInviteesMap.values());
  }

  openAgentDialog(availabilityInfosForAgents: string, customerName: string) {
    this.dialog.open(AgentsInfoDialog, {
      data: {
        title: customerName,
        html: availabilityInfosForAgents,
      },
    });
  }

  readonly carLicenseEnum = CarLicenseEnum;
  readonly sexEnum = SexEnum;

  transferPreventDirectAcceptCheckboxChange(transfer: EmploymentTransfer) {
    if (!this.loggedInUser) return;

    transfer.preventDirectAccept = !transfer.preventDirectAccept;
    this.form.markAsTouched();
    this.form.markAsDirty();
  }

  async setConditionalTransfer() {
    if (
      !this.loggedInCustomer?.customerTransfers?.transferConditions
        ?.employmentTransferConditions?.length
    )
      return;
    let transfers = this.transfers$.value;
    let employment: Employment | CreateEmploymentArgs | undefined =
      this.employment;
    if (!employment) {
      employment = await this.getEmploymentArgs();
    }

    transfers?.forEach((transfer) => {
      if (!employment) return;
      let conditionalTransferCheckResult = canTransferEmploymentToCustomer(
        employment,
        transfer.targetCustomer.id,
        this.loggedInCustomer!,
      );
      transfer.conditionallyBlocked =
        !conditionalTransferCheckResult.canTransfer;
      transfer.conditionallyBlockedUntil =
        conditionalTransferCheckResult.blockedUntil;
    });
    this.transfers$.next(transfers);
  }

  get canDecline(): boolean {
    const targetTransfer = this.employment?.transfers?.find(
      (transfer) => transfer.targetCustomer.id === this.loggedInCustomer?._id,
    );
    const hasBookedInvitee = this.employment?.invitees?.some(
      (invitee) =>
        invitee.status === EmploymentInviteeStatusEnum.BOOKED &&
        invitee.customer?.id === this.loggedInCustomer?._id,
    );
    return (
      (!this.isNewEmployment &&
        this.employment &&
        this.employment.status != EmploymentStatusEnum.ARCHIVED &&
        this.employment.status != EmploymentStatusEnum.CANCELED &&
        targetTransfer &&
        !targetTransfer.declinedAt &&
        !hasBookedInvitee) ||
      false
    );
  }

  declineTransfer() {
    Meteor.call(
      'declineEmploymentTransfer',
      this.employmentId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.toastService.add({ type: 'SUCCESS', message: 'transferDeclined' });
        this.historyBack();
      },
    );
  }

  trackByInviteeId(index: number, invitee: EmploymentInvitee) {
    return invitee.id;
  }

  hasUnsavedChanges(): boolean {
    if (this.form.dirty) {
      return true;
    }
    if (this.selectedInvitees.length > 0) {
      return true;
    }

    return false;
  }
}
